import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import ComponentCard from "../../common/ComponentCard";
import Label from "../Label";
import Input from "../input/InputField";
import TextArea from "../input/TextArea";
import Switch from "../switch/Switch";
import Button from "../../ui/button/Button";
import axios from "axios";
import { authService } from "../../../services/authService";

interface CheckoutBuilderFormData {
    active: boolean;
    name: string;
    description: string;
    offerId: string | null; // Uma oferta específica ou null para todas
    thankYouPageUrl: string;
    backRedirectUrl: string;
}

interface CheckoutBuilderFormProps {
    productId?: string;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
}

interface Offer {
    id: string;
    name: string;
    price: string | number;
    active: boolean;
    status: string | null;
}

export default function NewCheckoutBuilderForm({ productId, onSuccess, onError }: CheckoutBuilderFormProps) {
    const [formData, setFormData] = useState<CheckoutBuilderFormData>({
        active: true,
        name: "",
        description: "",
        offerId: null, // null = todas as ofertas
        thankYouPageUrl: "",
        backRedirectUrl: ""
    });

    const [loading, setLoading] = useState(false);
    const [fetchingOffers, setFetchingOffers] = useState(false);
    const [offers, setOffers] = useState<Offer[]>([]);
    const [selectAllOffers, setSelectAllOffers] = useState(true); // Inicia com "Todas as ofertas" selecionado
    const navigate = useNavigate();
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");

    // Buscar ofertas do produto quando o componente for montado
    useEffect(() => {
        const fetchOffers = async () => {
            if (!productId) {
                return;
            }

            setFetchingOffers(true);
            try {
                const response = await axios.get(
                    `${import.meta.env.VITE_API_URL}/api/products/${productId}/offers-with-links`,
                    {
                        headers: {
                            'Authorization': `Bearer ${authService.getToken()}`
                        }
                    }
                );

                if (response.status === 200 && response.data) {
                    // Extrair o array de ofertas da resposta e filtrar ofertas deletadas
                    const offersArray = response.data.offers || [];
                    const activeOffers = offersArray.filter((offer: any) => offer.status !== "deleted");
                    setOffers(activeOffers);
                }
            } catch (error: any) {
                console.error("Erro ao buscar ofertas:", error);
                setApiError(
                    error.response?.data?.message ||
                    error.response?.statusText ||
                    "Erro ao carregar as ofertas. Tente novamente."
                );
            } finally {
                setFetchingOffers(false);
            }
        };

        fetchOffers();
    }, [productId]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target;

        // Limpar o erro específico quando o campo é modificado
        if (errors[id]) {
            setErrors({
                ...errors,
                [id]: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");

        // Mapeamento de IDs para estrutura do formData
        switch (id) {
            case "inputName":
                setFormData({ ...formData, name: value });
                break;
            case "inputDescription":
                setFormData({ ...formData, description: value });
                break;
            case "inputThankYouPageUrl":
                setFormData({ ...formData, thankYouPageUrl: value });
                break;
            case "inputBackRedirectUrl":
                setFormData({ ...formData, backRedirectUrl: value });
                break;
            default:
                break;
        }
    };

    const handleTextAreaChange = (value: string) => {
        setFormData({ ...formData, description: value });

        // Limpar o erro se o campo foi preenchido
        if (errors.description && value) {
            setErrors({
                ...errors,
                description: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleSwitchChange = (checked: boolean) => {
        setFormData({
            ...formData,
            active: checked
        });

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleGoBack = () => {
        navigate(`/products/edit/${productId}?tab=checkout`);
    };

    const handleOfferChange = (selectedId: string) => {
        // Se "all" foi selecionado, offerId = null
        if (selectedId === 'all') {
            setSelectAllOffers(true);
            setFormData({
                ...formData,
                offerId: null
            });
        } else {
            // Oferta específica selecionada
            setSelectAllOffers(false);
            setFormData({
                ...formData,
                offerId: selectedId
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.inputName = "O nome do checkout é obrigatório";
        }

        if (!formData.description.trim()) {
            newErrors.description = "A descrição do checkout é obrigatória";
        }

        // Não precisa validar offerId pois pode ser null (todas as ofertas)

        setErrors(newErrors);

        // Se não houver erros, retorna true
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }

        // Validar formulário
        if (!validateForm()) {
            console.log("Validação do formulário falhou", errors);
            return;
        }

        setLoading(true);
        setApiError("");
        setApiSuccess("");

        try {
            // Preparar dados para API - NOVO FORMATO
            const checkoutData = {
                name: formData.name,
                description: formData.description,
                active: formData.active,
                productId: productId, // Obrigatório
                offerId: formData.offerId, // null = todas as ofertas, string = oferta específica
                thankYouPageUrl: formData.thankYouPageUrl,
                backRedirectUrl: formData.backRedirectUrl
            };

            // Criar checkout via /api/checkout-builder/{productId}
            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/api/checkout-builder/`,
                checkoutData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            if (response.status === 201 || response.status === 200) {
                setApiSuccess("Checkout criado com sucesso!");

                // Callback de sucesso se fornecido
                if (onSuccess) {
                    onSuccess(response.data);
                }
            } else {
                setApiError("Falha ao criar checkout. Por favor, tente novamente.");
            }
        } catch (error: any) {
            console.error("Erro ao criar checkout:", error);
            setApiError(error.response?.data?.message || "Erro ao criar o checkout. Tente novamente.");

            // Callback de erro se fornecido
            if (onError) {
                onError(error);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-1">
                <ComponentCard title="Configurações do seu checkout">
                    <>
                        {apiError && (
                            <div className="bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4">
                                {apiError}
                            </div>
                        )}

                        {apiSuccess && (
                            <div className="bg-green-50 dark:bg-green-900/10 text-green-600 dark:text-green-400 p-3 rounded-lg mb-4">
                                {apiSuccess}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            <div>
                                <Switch
                                    label="Ativo?"
                                    checked={formData.active}
                                    onChange={handleSwitchChange}
                                />
                            </div>

                            <div className="mt-6">
                                <Label htmlFor="inputName">Nome do template de checkout *</Label>
                                <Input
                                    type="text"
                                    id="inputName"
                                    placeholder="Informe o nome do template de checkout"
                                    onChange={handleInputChange}
                                    value={formData.name}
                                    error={!!errors.inputName}
                                    hint={errors.inputName}
                                />
                            </div>

                            <div className="mt-5">
                                <Label htmlFor="inputDescription">Descrição do template de checkout</Label>
                                <TextArea
                                    value={formData.description}
                                    onChange={handleTextAreaChange}
                                    rows={6}
                                    placeholder="Apenas para seu controle interno. Não é visível para o cliente."
                                    error={!!errors.description}
                                    hint={errors.description}
                                />
                            </div>

                            <div className="mt-5">
                                <Label htmlFor="inputOffer">Oferta para este checkout *</Label>

                                {fetchingOffers ? (
                                    <div className="mt-2 flex items-center text-sm text-gray-500">
                                        <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                        Carregando ofertas...
                                    </div>
                                ) : (
                                    <div className="mt-2">
                                        <select
                                            id="inputOffer"
                                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                            value={formData.offerId || 'all'}
                                            onChange={(e) => handleOfferChange(e.target.value)}
                                        >
                                            <option value="all">Todas as ofertas desse produto</option>
                                            {offers.map(offer => (
                                                <option key={offer.id} value={offer.id}>
                                                    {offer.name} - R$ {typeof offer.price === 'string' ? parseFloat(offer.price).toFixed(2) : offer.price.toFixed(2)}
                                                    {!offer.active ? ' (Inativa)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                        {selectAllOffers && (
                                            <p className="mt-2 text-sm text-gray-500">
                                                Este checkout será válido para todas as ofertas do produto
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="mt-5">
                                <Label htmlFor="inputThankYouPageUrl">Página de obrigado personalizada</Label>
                                <Input
                                    type="text"
                                    id="inputThankYouPageUrl"
                                    placeholder="https://seu-website.com"
                                    onChange={handleInputChange}
                                    value={formData.thankYouPageUrl}
                                    error={!!errors.thankYouPageUrl}
                                    hint={errors.thankYouPageUrl}
                                />
                            </div>

                            <div className="mt-5">
                                <Label htmlFor="inputBackRedirectUrl">URL de backredirect</Label>
                                <Input
                                    type="text"
                                    id="inputBackRedirectUrl"
                                    placeholder="https://seu-website.com"
                                    onChange={handleInputChange}
                                    value={formData.backRedirectUrl}
                                    error={!!errors.backRedirectUrl}
                                    hint={errors.backRedirectUrl}
                                />
                            </div>

                            <div className="mt-8 flex justify-between gap-4.5">
                                <Button
                                    variant="outline"
                                    size="md"
                                    onClick={handleGoBack}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    size="md"
                                    variant="primary"
                                    onClick={() => {
                                        handleSubmit();
                                    }}
                                    disabled={loading}
                                >
                                    {loading ? "Salvando..." : "Salvar"}
                                </Button>
                            </div>
                        </form>
                    </>
                </ComponentCard>
            </div>
        </div>
    );
}
